import { api, unwrap } from '../../shared/api/client';
import { ApiError } from '../../shared/api/errors';
import type { components } from '../../shared/api/schema.gen';

export type TDestination = components['schemas']['DestinationResDto'];
export type TDestinationCreate = components['schemas']['CreateDestinationReqDto'];
export type TDestinationConfig = components['schemas']['DestinationConfigDto'];
export type TDestinationUpdate = components['schemas']['UpdateDestinationReqDto'];
export type TDestinationCliSession = components['schemas']['DestinationCliSessionResDto'];
export type TProbe = components['schemas']['ProbeDestinationResDto'];
export type TProbePhase = TProbe['phase'];
export type TProbeAnswer = TProbe['answer'];

export const listDestinations = () =>
  unwrap(api.GET('/v1/destinations', { params: { query: { limit: 100, withTotal: true } } }));

export const searchDestinations = async (q: string) =>
  (await unwrap(api.GET('/v1/destinations', { params: { query: { q, limit: 50 } } }))).data.map(
    (item) => ({ value: item.id, label: item.name }),
  );

interface IZodIssue {
  path: (string | number)[];
  message: string;
}

const issuesOf = (details: unknown): IZodIssue[] => {
  const issues = (details as { issues?: unknown } | null)?.issues;
  if (!Array.isArray(issues)) return [];
  return issues.filter(
    (issue): issue is IZodIssue => Array.isArray(issue?.path) && typeof issue?.message === 'string',
  );
};

// `headers.x-foo` and `forwardHeaders.2` land on the list they belong to, not on a field nobody drew.
const FIELD_OF: Record<string, string> = {
  headers: 'headers',
  forwardHeaders: 'forwardHeaders',
};

const fieldOf = (path: (string | number)[]): string => {
  const [head] = path;
  if (typeof head === 'string' && FIELD_OF[head]) return FIELD_OF[head];
  return path.join('.');
};

const pinned =
  (path: string, method: string) =>
  (error: unknown): never => {
    if (!(error instanceof ApiError)) throw error;

    const raw = issuesOf(error.rawDetails);
    const details =
      error.code === 'destinations.name_taken'
        ? [{ field: 'name', message: error.message }]
        : raw.length > 0
          ? raw.map((issue) => ({ field: fieldOf(issue.path), message: issue.message }))
          : error.fieldErrors.map((one) => ({
              field: fieldOf(one.field.split('.')),
              message: one.message,
            }));
    if (details.length === 0) throw error;

    throw new ApiError(
      {
        code: error.code,
        httpCode: error.status,
        requestId: error.requestId,
        timestamp: new Date().toISOString(),
        path,
        method,
        message: error.message,
        details,
      },
      error.status,
    );
  };

export const createDestination = (body: TDestinationCreate): Promise<TDestination> =>
  unwrap(api.POST('/v1/destinations', { body })).catch(pinned('/v1/destinations', 'POST'));

export const getDestination = (id: string): Promise<TDestination> =>
  unwrap(api.GET('/v1/destinations/{id}', { params: { path: { id } } }));

export const updateDestination = (id: string, body: TDestinationUpdate): Promise<TDestination> =>
  unwrap(api.PATCH('/v1/destinations/{id}', { params: { path: { id } }, body })).catch(
    pinned(`/v1/destinations/${id}`, 'PATCH'),
  );

export const deleteDestination = (id: string): Promise<void> =>
  unwrap(api.DELETE('/v1/destinations/{id}', { params: { path: { id } } })).then(() => undefined);

export const probeDestination = (id: string): Promise<TProbe> =>
  unwrap(api.POST('/v1/destinations/{id}/probe', { params: { path: { id } } }));

// The runs API refuses a window wider than 31 days; a copy retrying longer is rare.
const RETRY_HORIZON_MS = 30 * 24 * 60 * 60 * 1000;
const WAITING_CAP = 200;

/** Runs with a copy still owing a delivery here — what an edit to the URL or key will reach. */
export const countWaiting = async (destinationId: string): Promise<number> => {
  const now = Date.now();
  const list = await unwrap(
    api.GET('/v1/runs', {
      params: {
        query: {
          from: new Date(now - RETRY_HORIZON_MS).toISOString(),
          to: new Date(now + 60_000).toISOString(),
          destinationId: [destinationId],
          copyStatus: ['running', 'waiting', 'retrying', 'paused', 'on_hold'],
          limit: WAITING_CAP,
        },
      },
    }),
  );
  return list.data.length;
};

// The list screen's page: a search runs on the server, so it reaches past the first hundred.
export const listDestinationsPage = (query: { q?: string; cursor?: string }) =>
  unwrap(
    api.GET('/v1/destinations', {
      params: { query: { limit: 100, withTotal: true, q: query.q, cursor: query.cursor } },
    }),
  );
